import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { EMPLOYMENT_EVENT_TYPES } from '../constants/employmentEventTypes';
import { EVENT_VALIDATION_STATES } from '../constants/employmentValidationStates';
import {
  eventDayFmt,
  employmentEventAnnotation,
  amendmentAnnotation,
  renewalSwitchDay
} from '../constants/employmentEventAnnotations';
import { contractLineLabel, previewEndEmployment, RESIGN_EFFECTS } from './endEmploymentPreview';

dayjs.extend(utc);

/**
 * ADR-136 phase 6 (§15) / ADR-185 D3–D4 — what the "Already scheduled" panel
 * lists, as pure functions (the component only renders the groups).
 */

// Pair-partners and administrative codes whose date is already carried by a
// line we do render (resign.submitted ↔ resign.finalized, rehire/hire ↔
// hire.finalized), or which only ever describe the past — skipped rather than
// shown twice.
const PAIRED_OR_PAST_CODES = [
  EMPLOYMENT_EVENT_TYPES.RESIGNATION_SUBMITTED,
  EMPLOYMENT_EVENT_TYPES.RESIGNATION_WITHDRAWN,
  EMPLOYMENT_EVENT_TYPES.REHIRE,
  EMPLOYMENT_EVENT_TYPES.HIRE,
  EMPLOYMENT_EVENT_TYPES.START_DELAYED,
  EMPLOYMENT_EVENT_TYPES.NO_SHOW,
  EMPLOYMENT_EVENT_TYPES.WITHDRAWN,
  EMPLOYMENT_EVENT_TYPES.RENEWAL_CANCELLED
];

const ymd = (value) => (value ? dayjs.utc(value).format('YYYY-MM-DD') : null);
const dmy = (value) => (value ? dayjs.utc(value).format('DD/MM/YYYY') : null);

/** ADR-185 D4 — a code nobody captioned yet reads as words, never as `employment.x.y`. */
export const humanizeEventCode = (code) => {
  const words = String(code || '')
    .replace(/^employment\./, '')
    .split(/[._-]+/)
    .filter(Boolean)
    .join(' ');
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Scheduled event';
};

export const describeEvent = (event) => {
  const code = event.eventType?.code;
  const state = event.eventValidationState;
  const day = eventDayFmt(event.effectiveOn);

  if (code === EMPLOYMENT_EVENT_TYPES.RENEWAL_SCHEDULED) {
    // ADR-182 D3 (S3) — the replacement happens on the switch day, not on the
    // cutover (the old contract's last day, ADR-179).
    const switchDay = eventDayFmt(renewalSwitchDay(event.payload?.cutoverDate || event.effectiveOn));
    return {
      primary: `Contract replacement — switch on ${switchDay}`,
      detail: employmentEventAnnotation(event)
    };
  }

  if (code === EMPLOYMENT_EVENT_TYPES.TERMINATION_FINALIZED) {
    return {
      primary: `Contract end scheduled for ${day}`,
      detail: 'Employment closes as of this date — enacted automatically on the first weekday morning after the grace window.'
    };
  }

  if (code === EMPLOYMENT_EVENT_TYPES.HIRE_FINALIZED) {
    return state === EVENT_VALIDATION_STATES.PENDING
      ? {
        primary: `Contract start awaiting confirmation — starts ${day}`,
        detail: 'The start is confirmed automatically on or after this date.'
      }
      : {
        primary: `New contract starts ${day}`,
        detail: 'Already recorded — the person moves onto this contract on that date.'
      };
  }

  // ADR-185 D4 — amendments are captioned, never printed as their code
  if (code === EMPLOYMENT_EVENT_TYPES.CONTRACT_AMENDED) {
    const number = event.payload?.amendmentNumber;
    return {
      primary: `${number ? `Amendment #${number}` : 'Amendment'} — effective ${day}`,
      detail: amendmentAnnotation(event)
    };
  }

  if (PAIRED_OR_PAST_CODES.includes(code)) {
    return null;
  }

  // An unknown-but-upcoming code: show it rather than hide it, in words.
  return { primary: `${humanizeEventCode(code)} — ${day}`, detail: null };
};

export const isUpcoming = (event, now = dayjs.utc()) => {
  if (event.eventValidationState === EVENT_VALIDATION_STATES.PENDING) {
    return true;
  }
  // Valid but future-dated: already recorded, takes effect on a later day
  // (e.g. a successor contract created by an immediate replace).
  return event.eventValidationState === EVENT_VALIDATION_STATES.VALID
    && Boolean(event.effectiveOn)
    && dayjs.utc(event.effectiveOn).isAfter(now, 'day');
};

/**
 * ADR-185 D4 — what THIS resignation does to an already-scheduled event, given
 * what it does to the event's contract. Mirrors the server: the contract
 * closes at its end (ADR-147 cancels a later Pending amendment), a voided
 * contract takes everything with it, a replacement switching after the
 * person has left is cancelled (person.employment-ended.js, ADR-185 D6) while
 * one switching before carries the resignation onto the new contract
 * (contract.replaced.js, §10), and a later scheduled end is moved to D.
 * @returns {string|null}
 */
export const resolutionByResignation = (event, contractEffect) => {
  if (!contractEffect || event.eventValidationState !== EVENT_VALIDATION_STATES.PENDING) {
    return null;
  }
  const { effect, endsOn } = contractEffect;
  if (effect === RESIGN_EFFECTS.VOIDED) {
    return 'Cancelled by this resignation — the contract is voided.';
  }
  if (!endsOn) {
    return null;
  }
  const code = event.eventType?.code;
  const on = ymd(event.effectiveOn);
  if (code === EMPLOYMENT_EVENT_TYPES.RENEWAL_SCHEDULED) {
    const cutover = ymd(event.payload?.cutoverDate || event.effectiveOn);
    if (!cutover) {
      return null;
    }
    // ADR-185 D6 / §10 — after the person has left: cancelled; before: it
    // switches, and the new contract ends on the resignation date too
    return cutover >= contractEffect.resignationDate
      ? 'Cancelled by this resignation — it would start after the person has left.'
      : `Switches as planned; the new contract also ends on ${dmy(contractEffect.resignationDate)}.`;
  }
  if (code === EMPLOYMENT_EVENT_TYPES.CONTRACT_AMENDED && on && on > endsOn) {
    return `Cancelled by this resignation — the contract closes on ${dmy(endsOn)} first.`;
  }
  if (code === EMPLOYMENT_EVENT_TYPES.TERMINATION_FINALIZED && on && on > endsOn) {
    return `Moved to ${dmy(endsOn)} by this resignation.`;
  }
  return null;
};

const episodeLabel = (episode, contract) => (contract
  ? contractLineLabel(contract)
  : contractLineLabel({
    contractNumber: episode.payload?.sourceReference?.contractId ?? null,
    contractType: episode.contractType,
    contractCompany: episode.contractCompany,
    startDate: episode.startDate
  }));

/**
 * @param {Object} args
 * @param {Array}  args.episodes          GET /employment/episodes/:id
 * @param {string} [args.episodeId]       ADR-113 §8 — scope to one contract (contract verbs)
 * @param {Array}  [args.contracts]       GET /employees/:id `contracts` — labels, primary, RESIGN effect
 * @param {string} [args.resignationDate] ADR-185 D4 — annotate what this resignation does
 * @returns {Array<{ episodeId, label, isPrimary, items: Array<{ id, primary, detail, resolution }> }>}
 *   Person-wide (no episodeId): one group per OPEN contract that has something
 *   scheduled, primary first. Scoped: at most one group.
 */
export const buildUpcomingSchedule = ({ episodes = [], episodeId = null, contracts = [], resignationDate = null, now } = {}) => {
  const contractById = new Map((contracts || []).map((c) => [c.id, c]));
  const effects = new Map();
  if (resignationDate && !episodeId) {
    previewEndEmployment({ kind: 'RESIGN', contracts, resignationDate }).forEach((row) => {
      effects.set(row.id, { effect: row.effect, endsOn: row.endsOn, resignationDate: ymd(resignationDate) });
    });
  }

  return (episodes || [])
    .filter((ep) => (episodeId ? ep.id === episodeId : !ep.endDate))
    .map((ep) => {
      const contract = contractById.get(ep.id) || null;
      const items = (ep.employmentEvents || [])
        .filter((e) => isUpcoming(e, now))
        .sort((a, b) => new Date(a.effectiveOn || 0) - new Date(b.effectiveOn || 0))
        .map((e) => {
          const described = describeEvent(e);
          return described
            ? { id: e.id, ...described, resolution: resolutionByResignation(e, effects.get(ep.id)) }
            : null;
        })
        .filter(Boolean);
      return {
        episodeId: ep.id,
        label: episodeLabel(ep, contract),
        isPrimary: Boolean(contract?.isPrimary),
        startDate: ep.startDate,
        items
      };
    })
    .filter((group) => group.items.length > 0)
    .sort((a, b) => {
      if (a.isPrimary !== b.isPrimary) {
        return a.isPrimary ? -1 : 1;
      }
      return new Date(a.startDate || 0) - new Date(b.startDate || 0);
    })
    .map((group) => ({ episodeId: group.episodeId, label: group.label, isPrimary: group.isPrimary, items: group.items }));
};
